import React, { useEffect, useRef, useState } from "react";
import { Textarea } from "@headlessui/react";
import Button from "../../form/front/Button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  communicationChatList,
  communicationSeen,
  CreateChat,
} from "../../../services/communicationService";
import { useSelector } from "react-redux";
import { useSocket } from "../../../providers/SocketContext";
import { X, Paperclip, Image as ImageIcon, FileText, File } from "lucide-react";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getFileType = (file) => {
  if (file.type.startsWith("image/")) return "image";
  if (file.type === "application/pdf") return "pdf";
  if (
    file.type.includes("word") ||
    file.name.endsWith(".doc") ||
    file.name.endsWith(".docx")
  )
    return "doc";
  return "file";
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ─── Attachment Preview Chip (before sending) ─────────────────────────────────

const AttachmentChip = ({ attachment, onRemove }) => {
  const type = getFileType(attachment.file);

  return (
    <div className="relative flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-2 pr-7 shadow-sm max-w-[200px]">
      <button
        onClick={onRemove}
        className="absolute top-1 right-1 w-4 h-4 rounded-full bg-gray-400 hover:bg-gray-600 flex items-center justify-center text-white"
      >
        <X size={10} />
      </button>

      {type === "image" ? (
        <img
          src={attachment.preview}
          alt={attachment.file.name}
          className="w-10 h-10 rounded object-cover shrink-0"
        />
      ) : (
        <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center shrink-0">
          {type === "pdf" ? (
            <FileText size={20} className="text-red-500" />
          ) : type === "doc" ? (
            <FileText size={20} className="text-blue-500" />
          ) : (
            <File size={20} className="text-gray-500" />
          )}
        </div>
      )}

      <div className="flex flex-col min-w-0">
        <p className="text-xs text-gray-700 font-medium truncate max-w-[110px]">
          {attachment.file.name}
        </p>
        <p className="text-[10px] text-gray-400">
          {formatFileSize(attachment.file.size)}
        </p>
      </div>
    </div>
  );
};

// ─── Message Attachment Display (after sending) ───────────────────────────────

const MessageAttachment = ({ attachment }) => {
  const url = typeof attachment === "string" ? attachment : attachment?.url;

  // API returns: filename, extension, size (as string bytes)
  const name =
    attachment?.filename ||
    attachment?.name ||
    attachment?.originalName ||
    "attachment";

  const ext = (
    attachment?.extension ||
    name?.split(".").pop() ||
    ""
  ).toLowerCase();

  const sizeBytes = parseInt(attachment?.size || "0", 10);
  const sizeLabel = sizeBytes
    ? sizeBytes < 1024 * 1024
      ? `${(sizeBytes / 1024).toFixed(1)} KB`
      : `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
    : null;

  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext);
  const isPdf = ext === "pdf";
  const isDoc = ["doc", "docx"].includes(ext);
  const isSheet = ["xls", "xlsx", "csv"].includes(ext);

  if (!url) return null;

  if (isImage) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        <img
          src={url}
          alt={name}
          className="max-w-[220px] max-h-[180px] rounded-lg object-cover border border-gray-200 hover:opacity-90 transition-opacity cursor-pointer"
        />
      </a>
    );
  }

  // ✅ PDF / Doc / File — downloadable card
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      download={name}
      className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-3 py-2.5 hover:bg-gray-50 active:bg-gray-100 transition-colors max-w-[240px] group"
    >
      {/* Icon */}
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          isPdf
            ? "bg-red-50"
            : isDoc
              ? "bg-blue-50"
              : isSheet
                ? "bg-green-50"
                : "bg-gray-100"
        }`}
      >
        <FileText
          size={20}
          className={
            isPdf
              ? "text-red-500"
              : isDoc
                ? "text-blue-500"
                : isSheet
                  ? "text-green-600"
                  : "text-gray-500"
          }
        />
      </div>

      {/* Info */}
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-xs font-medium text-gray-800 truncate leading-tight">
          {name}
        </span>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-[10px] text-gray-400 uppercase font-medium">
            {ext}
          </span>
          {sizeLabel && (
            <>
              <span className="text-[10px] text-gray-300">•</span>
              <span className="text-[10px] text-gray-400">{sizeLabel}</span>
            </>
          )}
        </div>
      </div>

      {/* Download arrow */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-gray-400 group-hover:text-gray-600 shrink-0 transition-colors"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    </a>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const MessageContent = ({
  activeChat,
  onBack,
  selectedCompany,
  companyIdFromUrl,
}) => {
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]); // [{ file, preview, id }]
  const { session } = useSelector((state) => state.authProvider);
  const [chatType, setChatType] = useState(null);
  const currentUserId = session?.user?._id;
  const queryClient = useQueryClient();
  const messagesEndRef = useRef(null);
  const activeChatRef = useRef(activeChat);
  const fileInputRef = useRef(null);
  const location = useLocation();
  const isCompanyInbox = location.pathname === "/inbox";

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Keep ref in sync with latest activeChat to avoid stale closure in socket
  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  // Mark chat as seen when opened
  useEffect(() => {
    if (!activeChat?._id || !currentUserId) return;

    communicationSeen({
      user: currentUserId,
      communicationGroupId: activeChat._id,
    })
      .then(() => {
        // queryClient.invalidateQueries({ queryKey: ["communication-list"] });
      })
      .catch(() => {
        // best-effort
      });
  }, [activeChat?._id, currentUserId]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      attachments.forEach((a) => {
        if (a.preview) URL.revokeObjectURL(a.preview);
      });
    };
  }, []);

  // const { data } = useQuery({
  //   queryKey: ["chat-messages", activeChat?._id],
  //   queryFn: async () => {
  //     const res = await communicationChatList({
  //       communicationGroupId: activeChat?._id,
  //     });

  //     return (res?.docs?.data || []).map((msg) => ({
  //       ...msg,
  //       attachmentInfo:
  //         msg.attachments?.length > 0
  //           ? msg.attachments
  //           : msg.attachmentInfo || [],
  //     }));
  //   },
  // });
  const { data } = useQuery({
    queryKey: ["chat-messages", activeChat?._id],
    enabled: !!activeChat?._id,
    queryFn: async () => {
      const res = await communicationChatList({
        communicationGroupId: activeChat?._id,
        purpose: "chat",
        page: 1,
        limit: 30,
        sortQuery: "-createdAt",
      });

      const listRaw = res?.docs?.data || [];
      const list =
        listRaw.length >= 2 &&
        new Date(listRaw[0]?.createdAt).getTime() >
          new Date(listRaw[listRaw.length - 1]?.createdAt).getTime()
          ? [...listRaw].reverse()
          : listRaw;

      // ✅ extract type from FIRST message
      if (list.length > 0) {
        const typeFromAPI =
          list[0]?.communicationGroupInfo?.type || list[0]?.type;
        setChatType(typeFromAPI);
      }

      return list.map((msg) => ({
        ...msg,
        attachmentInfo:
          msg.attachments?.length > 0
            ? msg.attachments
            : msg.attachmentInfo || [],
      }));
    },
  });

  // ─── Socket: receive incoming messages ─────────────────────────────────────
  useSocket("receiveMessage", (res) => {
    // console.log(res, "res");
    const newMsg = res?.data?.data;
    if (!newMsg) return;

    const currentChat = activeChatRef.current; // always fresh via ref
    if (newMsg.communicationGroupId !== currentChat?._id) return;
    if (newMsg.user === currentUserId) return;

    queryClient.setQueryData(["chat-messages", currentChat._id], (old = []) => {
      if (old.some((m) => m._id === newMsg._id)) return old;
      return [
        ...old,
        {
          _id: newMsg._id,
          content: newMsg.content,
          user: newMsg.user,
          createdAt: newMsg.createdAt,
          fullName:
            `${newMsg.userInfo?.firstName || ""} ${newMsg.userInfo?.lastName || ""}`.trim(),
          userInfo: newMsg.userInfo,
          attachmentInfo:
            newMsg.attachments?.length > 0
              ? newMsg.attachments
              : newMsg.attachmentInfo || [],
        },
      ];
    });
  });

  // ─── Send mutation ──────────────────────────────────────────────────────────
  const sendMessageMutation = useMutation({
    mutationFn: (payload) => CreateChat(payload),

    onMutate: async () => {
      await queryClient.cancelQueries(["chat-messages", activeChat?._id]);
      const previousMessages = queryClient.getQueryData([
        "chat-messages",
        activeChat?._id,
      ]);

      // Build local preview attachments for optimistic UI
      const optimisticAttachments = attachments.map((a) => ({
        url: a.preview, // blob URL for images, null for files
        name: a.file.name,
        originalName: a.file.name,
      }));

      queryClient.setQueryData(
        ["chat-messages", activeChat?._id],
        (old = []) => [
          ...old,
          {
            _id: `optimistic-${Date.now()}`,
            content: message,
            user: currentUserId,
            createdAt: new Date().toISOString(),
            fullName:
              `${session?.user?.firstName || ""} ${session?.user?.lastName || ""}`.trim(),
            userInfo: session?.user || null,
            attachmentInfo: optimisticAttachments,
            _optimistic: true,
          },
        ],
      );

      return { previousMessages };
    },

    onError: (err, _vars, context) => {
      queryClient.setQueryData(
        ["chat-messages", activeChat?._id],
        context.previousMessages,
      );
      toast.error(
        err?.data?.message || err?.message || "Failed to send message.",
      );
    },

    onSuccess: (res) => {
      // Use the real message returned by CreateChat instead of refetching
      const realMsg = res?.data;
      if (!realMsg) return;

      queryClient.setQueryData(
        ["chat-messages", activeChat?._id],
        (old = []) => {
          // Drop the optimistic entry
          const withoutOptimistic = old.filter(
            (m) => !String(m._id).startsWith("optimistic-"),
          );
          // Guard: socket may have already added it
          if (withoutOptimistic.some((m) => m._id === realMsg._id)) {
            return withoutOptimistic;
          }
          return [
            ...withoutOptimistic,
            {
              _id: realMsg._id,
              content: realMsg.content,
              user: realMsg.user,
              createdAt: realMsg.createdAt,
              fullName:
                `${realMsg.userInfo?.firstName || ""} ${realMsg.userInfo?.lastName || ""}`.trim(),
              userInfo: realMsg.userInfo,
              // attachments[] is the real field, attachmentInfo is always []
              attachmentInfo:
                realMsg.attachments?.length > 0
                  ? realMsg.attachments
                  : realMsg.attachmentInfo || [],
            },
          ];
        },
      );

      // queryClient.invalidateQueries({ queryKey: ["communication-list"] });
    },
  });

  const messages = data || [];

  useEffect(() => {
    if (messages.length > 0) scrollToBottom();
  }, [messages.length]);

  // ─── File selection handler ─────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
    const MAX_COUNT = 5;
    const remaining = MAX_COUNT - attachments.length;

    const toAdd = files.slice(0, remaining).filter((f) => {
      if (f.size > MAX_SIZE) {
        alert(`"${f.name}" exceeds the 10 MB limit.`);
        return false;
      }
      return true;
    });

    const newAttachments = toAdd.map((file) => ({
      file,
      preview: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
    }));

    setAttachments((prev) => [...prev, ...newAttachments]);
    e.target.value = ""; // reset so same file can be picked again
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const removed = prev.find((a) => a.id === id);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((a) => a.id !== id);
    });
  };

  // ─── Helpers ────────────────────────────────────────────────────────────────
  const getOtherMember = () => {
    const members = activeChat?.memberData || [];
    if (!members.length) return null;
    return members.find((m) => m._id !== currentUserId) || members[0];
  };

  const getAvatarUrl = (msg) => msg?.userInfo?.image?.url || null;
  const getDisplayName = (msg) => msg?.fullName || "Unknown";

  const groupMessagesByDate = (msgs) => {
    const groups = {};
    msgs.forEach((msg) => {
      const date = new Date(msg.createdAt).toDateString();
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  // ─── Send handler ───────────────────────────────────────────────────────────
  const getCompanyCustomerIdFromChat = () => {
    // For recipientType="company" the backend expects: customerId = COMPANY_ORG_ID
    const fromCommunicationData =
      activeChat?.communicationData?.customerId ||
      activeChat?.communicationData?.customer?._id ||
      activeChat?.communicationData?.organization?._id ||
      activeChat?.communicationData?.organizationId ||
      activeChat?.communicationData?.companyId;

    return (
      fromCommunicationData ||
      activeChat?.customerId ||
      activeChat?.customer?._id ||
      activeChat?.organization?._id ||
      activeChat?.organizationId ||
      activeChat?.companyId ||
      companyIdFromUrl ||
      null
    );
  };

  const handleSendMessage = () => {
    if (!message.trim() && attachments.length === 0) return;

    const otherUser = getOtherMember();
    if (!currentUserId || !otherUser?._id) return;

    const isOtherCompany =
      !otherUser?.firstName &&
      (!!otherUser?.content?.en?.title ||
        !!otherUser?.logo?.url ||
        !!otherUser?.name);

    const chatInboxType =
      activeChat?.communicationData?.inboxType || activeChat?.inboxType;
    const chatRecipientType =
      activeChat?.communicationData?.recipientType || activeChat?.recipientType;
    const chatCompanyId =
      activeChat?.communicationData?.companyId || activeChat?.companyId;

    const recipientTypeToSend = isCompanyInbox
      ? "user"
      : chatRecipientType || (isOtherCompany ? "company" : "user");

    let inboxTypeToSend = isCompanyInbox
      ? "company"
      : chatInboxType || (isOtherCompany ? "company" : "personal");

    // Backend rule: recipientType="company" must always use inboxType="company"
    if (recipientTypeToSend === "company") {
      inboxTypeToSend = "company";
    }

    const companyIdToSend = isCompanyInbox
      ? selectedCompany?.value
      : chatCompanyId;

    if (isCompanyInbox && !selectedCompany?.value) {
      toast.warning("Select a company to use the company inbox.");
      return;
    }

    const customerIdToSend =
      recipientTypeToSend === "company"
        ? getCompanyCustomerIdFromChat() ||
          (isOtherCompany ? otherUser?._id : null)
        : otherUser?._id;

    if (!customerIdToSend) {
      toast.error(
        recipientTypeToSend === "company"
          ? "Couldn't determine company id for this chat. Please reopen from the company page and try again."
          : "Couldn't determine recipient for this chat.",
      );
      return;
    }

    const senderName = isCompanyInbox
      ? selectedCompany?.name
      : `${session?.user?.firstName || ""} ${session?.user?.lastName || ""}`.trim() ||
        session?.user?.firstName ||
        "User";

    const typeToSend = chatType || "chat";

    if (attachments.length > 0) {
      const formData = new FormData();
      formData.append("type", typeToSend);
      formData.append("inboxType", inboxTypeToSend);
      formData.append("recipientType", recipientTypeToSend);
      formData.append("createdBy", currentUserId);
      formData.append("customerId", customerIdToSend);
      if (
        companyIdToSend &&
        inboxTypeToSend === "company" &&
        recipientTypeToSend === "user"
      ) {
        formData.append("companyId", companyIdToSend);
      }
      formData.append("message[name]", senderName);
      formData.append("message[content]", message);

      attachments.forEach((a, i) => {
        formData.append(`attachments[${i}]`, a.file);
      });

      sendMessageMutation.mutate(formData);
    } else {
      sendMessageMutation.mutate({
        type: typeToSend,
        inboxType: inboxTypeToSend,
        recipientType: recipientTypeToSend,
        createdBy: currentUserId,
        customerId: customerIdToSend,
        ...(companyIdToSend &&
        inboxTypeToSend === "company" &&
        recipientTypeToSend === "user"
          ? { companyId: companyIdToSend }
          : {}),
        message: { name: senderName, content: message },
      });
    }
    setMessage("");
    setAttachments([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const groupedMessages = groupMessagesByDate(messages);
  const otherUser = getOtherMember();
  const isSending = sendMessageMutation.isPending;
  const canSend = (message.trim() || attachments.length > 0) && !isSending;

  // ─── No chat selected ───────────────────────────────────────────────────────
  if (!activeChat) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        Select a conversation
      </div>
    );
  }

  return (
    <>
      {/* ── Messages scroll area ──────────────────────────────────────────── */}
      <div
        //  className="w-full bg-white min-h-[calc(100vh-365px)] max-h-[calc(100vh-365px)] overflow-auto flex flex-col"
        className={`w-full bg-white flex flex-col`}
      >
        {onBack && (
          <button
            onClick={onBack}
            className="md:hidden px-3 py-2 text-sm text-blue-600 absolute top-2 right-0"
          >
            <i class="fa-light fa-backward"></i> Back
          </button>
        )}
        {/* Other user info header */}
        <div className="sticky top-0 flex items-center p-3 bg-slate-100">
          {otherUser?.image?.url ? (
            <img
              src={otherUser.image.url}
              className="rounded-full w-10 h-10 object-cover"
              alt={otherUser.firstName}
            />
          ) : (
            <div className="w-9 h-9  rounded-full bg-palatte flex items-center justify-center text-white text-lg font-semibold">
              {otherUser?.firstName?.charAt(0)?.toUpperCase() || "U"}
            </div>
          )}
          <div className="pl-2">
            <p className="font-semibold text-gray-800 text-sm">
              {otherUser
                ? `${otherUser.firstName || ""} ${otherUser.lastName || ""}`
                : "Unknown"}
            </p>
            <p className="text-xs text-gray-500">{otherUser?.email || ""}</p>
          </div>
        </div>
        <div
          className={`overflow-y-auto overflow-x-hidden scrollbar ${
            attachments.length > 0
              ? "min-h-[calc(100vh-240px)] max-h-[calc(100vh-240px)]"
              : "min-h-[calc(100vh-310px)] max-h-[calc(100vh-310px)]"
          }`}
        >
          {/* Messages grouped by date */}
          <div className="p-3 space-y-4 pt-0">
            {Object.keys(groupedMessages).map((date) => (
              <div key={date}>
                {/* Date divider */}
                <div className="flex gap-4 items-center my-4">
                  <div className="w-full border-b border-gray-200" />
                  <div className="text-xs text-gray-500 whitespace-nowrap font-semibold">
                    {new Date(date).toLocaleDateString([], {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                  <div className="w-full border-b border-gray-200" />
                </div>

                <div className="space-y-4">
                  {groupedMessages[date].map((msg) => {
                    const isMe = msg.user === currentUserId;
                    const isOptimistic = !!msg._optimistic;
                    const hasAttachments = msg.attachmentInfo?.length > 0;

                    return (
                      <div
                        key={msg._id}
                        className={`flex gap-2 items-start w-full ${isMe ? "justify-end" : ""}`}
                      >
                        {/* Avatar */}
                        {!isMe &&
                          (getAvatarUrl(msg) ? (
                            <img
                              src={getAvatarUrl(msg)}
                              className="w-10 h-10 rounded-full object-cover shrink-0"
                              alt={getDisplayName(msg)}
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-palatte flex items-center justify-center text-white text-sm font-semibold shrink-0">
                              {getDisplayName(msg)?.charAt(0)?.toUpperCase()}
                            </div>
                          ))}

                        <div
                          className={`flex flex-col max-w-[70%] gap-1 ${
                            !isMe ? "items-start" : "items-end"
                          }`}
                        >
                          {/* Sender name + time */}
                          <div className="flex gap-2 items-center">
                            {!isMe && (
                              <h3 className="font-semibold text-gray-900 text-sm">
                                {msg.fullName}
                              </h3>
                            )}
                            <p className="text-gray-500 text-xs flex items-center gap-1">
                              {isOptimistic ? (
                                // Sending indicator instead of timestamp
                                <span className="flex items-center gap-1 text-gray-400">
                                  <svg
                                    className="animate-spin"
                                    width="10"
                                    height="10"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                  >
                                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                  </svg>
                                  Sending
                                </span>
                              ) : (
                                new Date(msg.createdAt).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              )}
                            </p>
                          </div>

                          {/* Attachments */}
                          {hasAttachments && (
                            <div
                              className={`flex flex-col gap-2 ${isMe ? "items-end" : "items-start"}`}
                            >
                              {msg.attachmentInfo.map((att, idx) => (
                                <MessageAttachment key={idx} attachment={att} />
                              ))}
                            </div>
                          )}

                          {/* Text bubble */}
                          {msg.content && (
                            <div
                              className={`rounded-lg p-2 text-sm break-words ${
                                isMe
                                  ? isOptimistic
                                    ? "bg-emerald-50 ml-auto border border-emerald-200 border-dashed text-emerald-700"
                                    : "bg-emerald-100 ml-auto"
                                  : "bg-gray-100"
                              }`}
                            >
                              {msg.content}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* ── Composer ──────────────────────────────────────────────────────── */}
      <div className="w-full border-t bg-white">
        {/* Attachment chips row */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 px-3 pt-3">
            {attachments.map((a) => (
              <AttachmentChip
                key={a.id}
                attachment={a}
                onRemove={() => removeAttachment(a.id)}
              />
            ))}
          </div>
        )}

        <div className="flex items-end gap-1 bg-gray-100 md:px-3 py-2 shadow-sm">
          <div className="flex items-center">
            {/* Image picker */}
            <button
              type="button"
              onClick={() => {
                fileInputRef.current.accept = "image/*";
                fileInputRef.current.multiple = true;
                fileInputRef.current.click();
              }}
              disabled={attachments.length >= 5 || isSending}
              title="Attach image"
              className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 transition"
            >
              <ImageIcon size={18} />
            </button>

            {/* File picker */}
            <button
              type="button"
              onClick={() => {
                fileInputRef.current.accept =
                  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.csv";
                fileInputRef.current.multiple = true;
                fileInputRef.current.click();
              }}
              disabled={attachments.length >= 5 || isSending}
              title="Attach file"
              className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 transition"
            >
              <Paperclip size={18} />
            </button>

            {/* Single shared hidden input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />

            {attachments.length > 0 && (
              <span className="text-[11px] text-gray-400 ml-1 select-none">
                {attachments.length}/5
              </span>
            )}
          </div>

          {/* Text input */}
          <Textarea
            rows={1}
            placeholder="Write a message…"
            // placeholder="Write a message… (Enter to send, Shift+Enter for new line)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSending}
            className="w-full resize-none outline-none text-sm text-gray-800 placeholder-gray-500 rounded-md border-0 !bg-white px-3 py-2 disabled:opacity-50 focus:outline-none focus:border-none focus:ring-0"
          />

          <div className="">
            <Button
              // buttonLabel={isSending ? "Sending…" : "Send"}
              buttonIcon={"fa-solid fa-paper-plane"}
              buttonClasses="rounded-md w-9 h-9 rounded-full"
              buttonFunction={handleSendMessage}
              disabled={!canSend}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default MessageContent;
